/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Collapse,
  Table,
  Button,
  Spin,
  message,
  Checkbox,
  Input,
  Row,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { CollapseProps } from 'antd/lib/collapse';
import { groupBy, uniq } from 'lodash';
import { useIntl } from 'react-intl';
import { queryCache } from 'react-query';

import UploadContext from '~src/contexts/UploadContext';
import icon from '~components/_common/icon/icon';
import IconButton from '~components/_common/IconButton/IconButton';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import FileProgressContext, {
  AttachmentProgress,
  FileProgress,
  FolderEntry,
} from '~src/contexts/FileProgressContext';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { useProjectId } from '~src/hooks/useProjectId';
import { buildFileLocation, insertMissingPathLevels } from '~src/utils/path';
import { formatBytes } from '~src/utils/formatBytes';
import { FileX } from '~src/types/upload';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import {
  createCommit,
  createFolder,
  deleteCommit,
  deleteFolder,
  idOrNull,
} from '~src/services/api';
import { appLog } from '~src/utils/log';
import { fadeInKeyframes } from '~src/styles/animations';
import { __DEV__ } from '~src/config';
import { EditableCell } from '~components/_common/EditableCell/EditableCell';
import { SimpleObject } from '~src/types';
import { DB_STATE } from '~src/types/enums';
import { usePreventNavigation } from '~src/hooks/usePreventNavigation';
import { useHistory, useLocation } from 'react-router';
import { DbCommit } from '~src/types/db';
import MobileViewContext from '~src/contexts/MobileViewContext';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { useIsPath } from '~src/hooks/useIsPath';
import { Select } from 'antd';
import { SelectProps } from 'antd/lib/select';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';

export interface DetailedUploadPanelProps {
  /** Collapse state change callback */
  onCollapse?: CollapseProps['onChange'];
  mobile?: boolean;
  skipCleanupRef?: { current: boolean };
}
const DetailedUploadPanel: React.FC<DetailedUploadPanelProps> = ({
  onCollapse,
  mobile,
  skipCleanupRef,
}) => {
  const style = useStyles(styleFn);
  const intl = useIntl();
  const isOnTender = useIsPath('/tenders');
  const currentFolder = useCurrentFolder();
  const currentFolderRef = useRef(currentFolder);
  const projectId = useProjectId();

  const filesQueryBaseKeys = {
    folderId: idOrNull(currentFolder?.id),
    withVersions: true,
    before:
      window.sessionStorage.getItem('timeMachineDate') !== null
        ? Number(window.sessionStorage.getItem('timeMachineDate'))
        : undefined,
  };
  const filesQuery = useFiles(filesQueryBaseKeys, {
    enabled: false,
  });

  const foldersQuery = useFolders({
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
  });

  const { uploadContext, setUploadContext } = useContext(UploadContext);
  const { setFileProgressContext } = useContext(FileProgressContext);
  const { showMobileView } = useContext(MobileViewContext);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 - files table, 2 - commit
  const [commitMessage, setCommitMessage] = useState(
    __DEV__ ? 'This is a test commit' : ''
  );

  const [importantVal, setImportantVal] = useState(false);
  const [fileDescriptions, setFileDescriptions] = useState<
    SimpleObject<string>
  >({});

  const [fileRevisions, setFileRevisions] = useState<SimpleObject<string>>({});
  usePreventNavigation({
    modalFuncProps: {
      content: intl.formatMessage({ id: 'prevent-navigation-upload-message' }),
    },
  });

  const [fileTags, setFileTags] = useState<SimpleObject<number[]>>({});
  const archiveSettings = useArchiveSettings();
  const tagsData = archiveSettings.data?.resTags?.filter(
    (tag) => tag.state !== DB_STATE.REMOVED
  );

  useEffect(() => {
    uploadContext?.forEach((element) => {
      const initialDescription = filesQuery.data?.find(
        (item) => item.title === element.name
      )?.description;
      // @ts-ignore
      const initialRevision = filesQuery.data?.find(
        (item) => item.title === element.name
      )?.versions[0]?.version;

      // console.log(initialDescription, initialRevision, fileRevisions);

      if (
        initialDescription &&
        initialDescription !== fileDescriptions[element.uid] &&
        !fileDescriptions[element.uid]
      ) {
        setFileDescriptions((prevState) => ({
          ...prevState,
          [element.uid]: initialDescription,
        }));
      }

      if (
        initialRevision &&
        initialRevision !== fileRevisions[element.uid] &&
        !fileRevisions[element.uid]
      ) {
        const nextRevision = incrementRevision(initialRevision);
        setFileRevisions((prevState) => ({
          ...prevState,
          [element.uid]: nextRevision,
        }));
      }
    });
  }, [uploadContext]);

  useEffect(() => {
    return () => {
      if (skipCleanupRef?.current) {
        skipCleanupRef.current = false;
        return;
      }

      setUploadContext([]);
    };
  }, [setUploadContext, skipCleanupRef]);

  const beforeUnloadListener = (e) => {
    e.preventDefault();
    return (e.returnValue = intl.formatMessage({ id: 'you-sure' }));
  };

  useEffect(() => {
    window.addEventListener('beforeunload', beforeUnloadListener, {
      capture: true,
    });
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadListener, {
        capture: true,
      });
    };
  }, []);

  // dismiss panel on navigation
  useEffect(() => {
    if (currentFolder?.id !== currentFolderRef.current?.id) {
      setUploadContext([]);
    }
  }, [currentFolder, setUploadContext]);

  useEffect(() => {
    // resize file table on step change
    onCollapse && onCollapse('1');
    // run effect only when `step` changes
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTimeout(() => setStep(1), 200);
  }, [uploadContext.length]);

  // dismiss panel on navigation
  if (currentFolder?.id !== currentFolderRef.current?.id) return null;

  const onEditInputCopy = (
    state: SimpleObject<string>,
    setState: Dispatch<SetStateAction<SimpleObject<string>>>,
    focusedFileUid: string
  ) => {
    const focusedFileIndex = uploadContext.findIndex(
      ({ uid }) => uid === focusedFileUid
    );

    const updatedState = { ...state };
    uploadContext.forEach((fileToUpload, index) => {
      if (focusedFileIndex < index) {
        updatedState[fileToUpload.uid] = updatedState[focusedFileUid];
      }
    });
    setState(updatedState);
  };

  const onEditInputBlur = (
    setState: Dispatch<SetStateAction<SimpleObject<string>>>,
    {
      focusedFileUid,
      inputValue,
    }: { focusedFileUid: string; inputValue: string }
  ) => {
    setState((currentState) => ({
      ...currentState,
      [focusedFileUid]: inputValue,
    }));
  };

  const columns: ColumnsType<FileX> = [
    {
      // title: intl.formatMessage({ id: 'remove' }),
      dataIndex: 'uid',
      key: 'remove',
      width: 50,
      align: 'center',
      onCell: () => ({ style: { padding: '0 5px' } }),
      render: (uid) => (
        <IconButton
          iconStyle="far"
          icon="times-circle"
          customStyle={style.removeIcon}
          click={() =>
            setUploadContext((prevList) =>
              prevList.filter((v) => v.uid !== uid)
            )
          }
          tooltip={intl.formatMessage({ id: 'remove-file' })}
        />
      ),
    },
    {
      title: intl.formatMessage({ id: 'file-name' }),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: intl.formatMessage({ id: 'description' }),
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (_, file) => {
        return (
          <div css={style.editableCell}>
            <EditableCell
              customStyle={style.editableInput}
              initialValue={fileDescriptions[file.uid]}
              onDone={(val) => {
                appLog(`onDone(description) -> ${file.uid} -> val`, val);

                onEditInputBlur(setFileDescriptions, {
                  focusedFileUid: file.uid,
                  inputValue: val,
                });
              }}
            />
            {uploadContext.length > 1 && (
              <IconButton
                icon={['far', 'level-down']}
                customStyle={style.icons}
                click={() =>
                  onEditInputCopy(
                    fileDescriptions,
                    setFileDescriptions,
                    file.uid
                  )
                }
                tooltip={intl.formatMessage({ id: 'revision-copy' })}
              />
            )}
          </div>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'revision' }),
      dataIndex: 'revision',
      key: 'revision',
      width: 200,
      render: (_, file) => (
        <div css={style.editableCell}>
          <EditableCell
            customStyle={style.editableInput}
            initialValue={fileRevisions[file.uid]}
            onDone={(val) => {
              appLog(`onDone(revision) -> ${file.uid} -> val`, val);

              onEditInputBlur(setFileRevisions, {
                focusedFileUid: file.uid,
                inputValue: val,
              });
            }}
          />
          {uploadContext.length > 1 && (
            <IconButton
              icon={['far', 'level-down']}
              customStyle={style.icons}
              click={() =>
                onEditInputCopy(fileRevisions, setFileRevisions, file.uid)
              }
              tooltip={intl.formatMessage({ id: 'revision-copy' })}
            />
          )}
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'tags' }),
      key: 'tags',
      width: 250,
      render: (_, file) => (
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Select tags"
          options={tagsData?.map((tag) => ({
            value: tag.id,
            label: tag.title,
          }))}
          value={fileTags[file.uid] || []}
          onChange={(values) => {
            setFileTags((prev) => ({
              ...prev,
              [file.uid]: values,
            }));
          }}
        />
      ),
    },
    {
      title: intl.formatMessage({ id: 'path' }),
      dataIndex: 'webkitRelativePath',
      key: 'path',
      render: (x, file) => buildFileLocation(file, currentFolder),
    },
    {
      title: intl.formatMessage({ id: 'size' }),
      key: 'size',
      dataIndex: 'size',
      width: 90,
      render: (size) => formatBytes(size, 1),
    },
  ];

  const tenderColumns: ColumnsType<FileX> = [
    {
      // title: intl.formatMessage({ id: 'remove' }),
      dataIndex: 'uid',
      key: 'remove',
      width: 50,
      align: 'center',
      onCell: () => ({ style: { padding: '0 5px' } }),
      render: (uid) => (
        <IconButton
          iconStyle="far"
          icon="times-circle"
          customStyle={style.removeIcon}
          click={() =>
            setUploadContext((prevList) =>
              prevList.filter((v) => v.uid !== uid)
            )
          }
          tooltip={intl.formatMessage({ id: 'remove-file' })}
        />
      ),
    },
    {
      title: intl.formatMessage({ id: 'file-name' }),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: intl.formatMessage({ id: 'description' }),
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (_, file) => {
        return (
          <div css={style.editableCell}>
            <EditableCell
              customStyle={style.editableInput}
              initialValue={fileDescriptions[file.uid]}
              onDone={(val) => {
                appLog(`onDone(description) -> ${file.uid} -> val`, val);

                onEditInputBlur(setFileDescriptions, {
                  focusedFileUid: file.uid,
                  inputValue: val ?? '',
                });
              }}
            />
            {uploadContext.length > 1 && (
              <IconButton
                icon={['far', 'level-down']}
                customStyle={style.icons}
                click={() =>
                  onEditInputCopy(
                    fileDescriptions,
                    setFileDescriptions,
                    file.uid
                  )
                }
                tooltip={intl.formatMessage({ id: 'revision-copy' })}
              />
            )}
          </div>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'version' }),
      dataIndex: 'version',
      key: 'version',
      width: 200,
      render: (_, file) => (
        <div css={style.editableCell}>
          <EditableCell
            customStyle={style.editableInput}
            initialValue={fileRevisions[file.uid]}
            onDone={(val) => {
              appLog(`onDone(version) -> ${file.uid} -> val`, val);

              onEditInputBlur(setFileRevisions, {
                focusedFileUid: file.uid,
                inputValue: val ?? '',
              });
            }}
          />
          {uploadContext.length > 1 && (
            <IconButton
              icon={['far', 'level-down']}
              customStyle={style.icons}
              click={() =>
                onEditInputCopy(fileRevisions, setFileRevisions, file.uid)
              }
              tooltip={intl.formatMessage({ id: 'revision-copy' })}
            />
          )}
        </div>
      ),
    },
    // {
    //   title: intl.formatMessage({ id: 'tags' }),
    //   key: 'tags',
    //   width: 250,
    //   render: (_, file) => (
    //     <Select
    //       mode="multiple"
    //       style={{ width: '100%' }}
    //       placeholder="Select tags"
    //       options={tagsData?.map((tag) => ({
    //         value: tag.id,
    //         label: tag.title,
    //       }))}
    //       value={fileTags[file.uid] || []}
    //       onChange={(values) => {
    //         setFileTags((prev) => ({
    //           ...prev,
    //           [file.uid]: values,
    //         }));
    //       }}
    //     />
    //   ),
    // },
    {
      title: intl.formatMessage({ id: 'size' }),
      key: 'size',
      dataIndex: 'size',
      width: 90,
      render: (size) => formatBytes(size, 1),
    },
  ];
  const createFolders = async (
    files: typeof uploadContext,
    commitId: DbCommit['id']
  ) => {
    setIsLoading(true);

    // parse unique folder path lists
    const folderPaths = uniq(
      files.map((v) =>
        v.webkitRelativePath?.endsWith(v.name)
          ? v.webkitRelativePath.slice(0, v.webkitRelativePath.lastIndexOf('/'))
          : v.webkitRelativePath
      )
    ).map((path) => path?.split('/').filter(Boolean));

    // group path lists by length, then make a list of them
    const groups = groupBy(folderPaths as string[][], 'length');

    // results in levels of folders
    // levels have to be created sequentially, because they need the parent of previous level
    // folders in a given level can be created simultaneously, as their parents are all on the upper level
    const levels = Object.values(insertMissingPathLevels(groups));

    /** holds parents of files */
    const folderList: Array<
      FolderEntry & {
        /** Used to match back to file webkitRelativePath */
        path: string;
      }
    > = [];

    // keep parents refs, as list of results from previous step
    // this is because at each level folders may have different parents
    let levelParents: FolderEntry[] = currentFolder
      ? [{ dbFolder: currentFolder, isNew: false }]
      : [];

    try {
      for (const level of levels) {
        levelParents = await Promise.all(
          level.map(async (folderPathList) => {
            /** used to match back to its file children */
            const path = folderPathList.join('/');
            const folderName = folderPathList[folderPathList.length - 1]; // last element
            const parentName = folderPathList[folderPathList.length - 2]; // `undefined` on first level

            // match parent
            const parent = parentName
              ? levelParents.find((v) => v.dbFolder.name === parentName)
              : levelParents[0];
            const parentId = parent?.dbFolder.id || null;

            // no parent, or existing parent => find folder match
            if (!parent?.isNew) {
              const match = foldersQuery.data?.find(
                (v) => v.parent_id === parentId && v.name === folderName
              );

              if (match) {
                // match found, return it
                const folder = { dbFolder: match, isNew: false };
                folderList.push({ ...folder, path });
                return folder;
              }
            }

            // parent.isNew or no match -> CREATE FOLDER
            const created = await createFolder({
              projectId: String(projectId),
              folderData: {
                name: folderName,
                parent_id: parentId,
                is_locked: false,
                description: '',
                state: DB_STATE.AVAILABLE,
              },
              commit_id: commitId,
            });

            // return created folder
            const folder = { dbFolder: created, isNew: true };
            folderList.push({ ...folder, path });
            return folder;
          })
        );
      }

      return folderList;
    } catch (err) {
      message.error(intl.formatMessage({ id: 'folder-create-failed' }));
      setIsLoading(false);

      // delete new folders
      const foldersToDelete = folderList.filter((v) => v.isNew);
      if (foldersToDelete.length) {
        Promise.all(
          folderList
            .filter((v) => v.isNew)
            .map((f) =>
              deleteFolder({
                projectId: String(projectId),
                folder: f.dbFolder,
                commitId: commitId,
              }).catch(() => null)
            )
        ).then(() => queryCache.invalidateQueries('folders'));
      }

      appLog('UploadPanel -> createFolders -> err', err);
      throw err;
    }
  };

  const isValid = () => {
    // return commitMessage.length > 1;
    return true;
  };

  const doCreateCommit = async () => {
    if (!projectId) throw new Error('Missing project id.');

    const commitData = {
      description: commitMessage,
      is_important: importantVal,
      state: DB_STATE.DRAFT,
    };

    return createCommit({
      projectId: String(projectId),
      commitData,
    }).catch((err) => {
      message.error(intl.formatMessage({ id: 'commit-create-failed' }));
      appLog('UploadPanel -> createCommit -> commit', commitData);
      appLog('UploadPanel -> createCommit -> err', err);
      throw err;
    });
  };

  const handleUploadClick = async () => {
    if (!projectId || !isValid()) return;
    setIsLoading(true);

    const nestedFiles = uploadContext.filter(
      (v) => v.webkitRelativePath && v.webkitRelativePath !== v.name
    );

    try {
      const commit = await doCreateCommit();

      // create folders for nested files
      const folderList = nestedFiles.length
        ? await createFolders(nestedFiles, commit.id).catch((foldersErr) => {
            deleteCommit({
              projectId: String(projectId),
              commit,
            }).catch(() => undefined);
            throw foldersErr;
          })
        : [];

      // map uploadContext items into fileProgressContext items
      // the upload is handled in FileProgressItem
      setFileProgressContext((prevFileProgress) => [
        ...uploadContext.map((file, i) => {
          const id = `${file.name}-${Date.now()}-${i}`;

          let folder: FolderEntry | undefined = currentFolder
            ? { dbFolder: currentFolder, isNew: false }
            : undefined;
          if (
            file.webkitRelativePath &&
            file.webkitRelativePath !== file.name
          ) {
            const path = file.webkitRelativePath.endsWith(file.name)
              ? file.webkitRelativePath.slice(
                  0,
                  file.webkitRelativePath.lastIndexOf('/')
                )
              : file.webkitRelativePath;
            folder = folderList.find((v) => v.path === path);
          }
          const timestamp = new Date().getTime();
          const fileExtension = file.name.split('.').pop();
          const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
          const modifiedFileName = `${fileNameWithoutExtension}-${timestamp}.${fileExtension}`;

          const modifiedFile = new File([file], modifiedFileName, {
            type: file.type,
            lastModified: file.lastModified,
          });

          const blacklistNames = ['image'];
          const isBlacklistedName = blacklistNames.includes(
            fileNameWithoutExtension.toLowerCase()
          );

          const fileProgress: FileProgress = {
            id,
            commit,
            file: !isBlacklistedName ? file : modifiedFile,
            description: fileDescriptions[file.uid],
            versionStr: fileRevisions[file.uid],
            folder,
            tags: fileTags[file.uid] || [],
            progress: {
              bytesTotal: 0,
              bytesUploaded: 0,
              percentage: '0',
            },
            entityCreated: false,
            isDone: false,
            projectId,
            shouldStartUpload: i < 3 ? true : false,
          };

          return fileProgress;
        }),
        ...prevFileProgress,
      ]);

      setUploadContext([]);
    } catch (err) {
      message.error(`An error occurred: ${err}`);
      setIsLoading(false);
    }
  };

  const handleTenderUploadClick = async () => {
    if (!projectId || !isValid()) return;
    setIsLoading(true);

    const tenderIdMatch = window?.location?.pathname?.match(/tenders\/(\d+)/);
    const tenderId = tenderIdMatch ? String(tenderIdMatch[1]) : '';
    try {
      // Map uploadContext to the structure expected by FileProgressItem
      const fileProgressItems = uploadContext.map((file, index) => {
        const fileProgress: AttachmentProgress = {
          id: `${file.name}-${Date.now()}-${index}`,
          file: file,
          description: fileDescriptions[file.uid] || '',
          versionStr: fileRevisions[file.uid] || '',
          tags: fileTags[file.uid] || [],
          // Add any other properties needed by FileProgressItem for the upload
          progress: {
            bytesTotal: file.size,
            bytesUploaded: 0,
            percentage: '0',
          },
          entityCreated: false,
          isDone: false,
          commit: {
            id: 0,
            project_id: 0,
            description: 'string',
            is_important: true,
            state: 1,
            created_by: 0,
            created_at: 0,
          },
          projectId,
          tenderId, // Include tenderId if needed for the upload logic in FileProgressItem
          shouldStartUpload: true, // Indicate that the upload should start immediately
        };
        return fileProgress;
      });

      // Update the file progress context with the new items
      setFileProgressContext((prevFileProgress) => [
        ...prevFileProgress,
        ...fileProgressItems,
      ]);

      // Optionally, clear the upload context or perform other UI updates after setting up the uploads
      // setUploadContext([]);
      setIsLoading(false); // Update loading state as appropriate
    } catch (error) {
      console.error('Failed to prepare tender attachments upload:', error);
      setIsLoading(false);
      // Handle error (e.g., show a message to the user)
    }
  };

  const panelHeader = (
    <Row align="middle">
      <IconButton
        iconStyle="far"
        icon="info-circle"
        customStyle={style.icons}
        containerStyle={{ marginLeft: 'auto' }}
        backgroundOff
        tooltip={intl.formatMessage({ id: 'upload-info' })}
      />
      {intl.formatMessage({ id: 'details' })}
    </Row>
  );

  const panelWarning = (
    <Row
      align="middle"
      style={{ marginBottom: '10px' }}
      onClick={() =>
        appLog(`folderRef`, currentFolderRef.current?.option?.is_tender_folder)
      }>
      {intl.formatMessage({ id: 'tender-folder-warning' })}
    </Row>
  );

  const renderMobileFileCard = (file: FileX, index: number) => {
    const filePath = buildFileLocation(file, currentFolder);
    const revisionLabel = isOnTender
      ? intl.formatMessage({ id: 'version' })
      : intl.formatMessage({ id: 'revision' });

    return (
      <div key={file.uid} css={style.mobileFileCard}>
        <div css={style.mobileFileHeader}>
          <div css={style.mobileFileIndex}>{index + 1}</div>

          <div css={style.mobileFileInfo}>
            <div css={style.mobileFileName}>{file.name}</div>

            <div css={style.mobileFileMeta}>
              <span>{formatBytes(file.size, 1)}</span>
              {filePath ? (
                <span css={style.mobileFilePath}>{filePath}</span>
              ) : null}
            </div>
          </div>

          <IconButton
            iconStyle="far"
            icon="times-circle"
            customStyle={style.mobileRemoveIcon}
            click={() =>
              setUploadContext((prevList) =>
                prevList.filter((v) => v.uid !== file.uid)
              )
            }
            tooltip={intl.formatMessage({ id: 'remove-file' })}
          />
        </div>

        <div css={style.mobileField}>
          <div css={style.mobileFieldLabel}>
            {intl.formatMessage({ id: 'description' })}
          </div>

          <div css={style.mobileEditableRow}>
            <EditableCell
              customStyle={style.mobileEditableInput}
              initialValue={fileDescriptions[file.uid]}
              onDone={(val) => {
                appLog(`onDone(description) -> ${file.uid} -> val`, val);

                onEditInputBlur(setFileDescriptions, {
                  focusedFileUid: file.uid,
                  inputValue: val ?? '',
                });
              }}
            />

            {uploadContext.length > 1 && (
              <IconButton
                icon={['far', 'level-down']}
                customStyle={style.mobileCopyIcon}
                click={() =>
                  onEditInputCopy(
                    fileDescriptions,
                    setFileDescriptions,
                    file.uid
                  )
                }
                tooltip={intl.formatMessage({ id: 'revision-copy' })}
              />
            )}
          </div>
        </div>

        <div css={style.mobileField}>
          <div css={style.mobileFieldLabel}>{revisionLabel}</div>

          <div css={style.mobileEditableRow}>
            <EditableCell
              customStyle={style.mobileEditableInput}
              initialValue={fileRevisions[file.uid]}
              onDone={(val) => {
                appLog(`onDone(revision/version) -> ${file.uid} -> val`, val);

                onEditInputBlur(setFileRevisions, {
                  focusedFileUid: file.uid,
                  inputValue: val ?? '',
                });
              }}
            />

            {uploadContext.length > 1 && (
              <IconButton
                icon={['far', 'level-down']}
                customStyle={style.mobileCopyIcon}
                click={() =>
                  onEditInputCopy(fileRevisions, setFileRevisions, file.uid)
                }
                tooltip={intl.formatMessage({ id: 'revision-copy' })}
              />
            )}
          </div>
        </div>

        {!isOnTender ? (
          <div css={style.mobileField}>
            <div css={style.mobileFieldLabel}>
              {intl.formatMessage({ id: 'tags' })}
            </div>

            <Select
              mode="multiple"
              css={style.mobileSelect}
              placeholder="Select tags"
              options={tagsData?.map((tag) => ({
                value: tag.id,
                label: tag.title,
              }))}
              value={fileTags[file.uid] || []}
              onChange={(values) => {
                setFileTags((prev) => ({
                  ...prev,
                  [file.uid]: values,
                }));
              }}
            />
          </div>
        ) : null}
      </div>
    );
  };

  if (mobile) {
    return (
      <div css={showMobileView ? style.containerMobile : style.container}>
        <Collapse
          bordered={false}
          defaultActiveKey={['uploadPanel']}
          css={style.mobileProperties}
          expandIconPosition="right"
          onChange={(...args) =>
            onCollapse && setTimeout(() => onCollapse(...args), 200)
          }>
          <Collapse.Panel header={panelHeader} key="uploadPanel">
            <div css={style.mobilePanelContainer}>
              <div css={style.mobilePanelTitle}>
                <div>
                  {intl.formatMessage(
                    { id: 'upload-panel-header' },
                    { noOfFiles: uploadContext.length }
                  )}
                </div>

                {/*<div css={style.mobileStepBadge}>*/}
                {/*  {step === 1*/}
                {/*    ? intl.formatMessage({ id: 'details' })*/}
                {/*    : intl.formatMessage({ id: 'commit' })}*/}
                {/*</div>*/}
              </div>

              <div css={style.mobileContentContainer}>
                {step === 1 ? (
                  <Fragment>
                    {currentFolderRef.current?.option?.is_tender_folder
                      ? panelWarning
                      : null}

                    <div css={style.mobileFilesList}>
                      {uploadContext.map((file, index) =>
                        renderMobileFileCard(file, index)
                      )}
                    </div>

                    <div css={style.mobileActions}>
                      <Button
                        type="default"
                        onClick={() => setUploadContext([])}
                        css={style.mobileActionButton}>
                        {intl.formatMessage({ id: 'cancel' })}
                      </Button>

                      {isOnTender ? (
                        <Button
                          type="primary"
                          onClick={handleTenderUploadClick}
                          loading={isLoading}
                          disabled={isLoading}
                          css={style.mobileActionButton}>
                          {intl.formatMessage({ id: 'upload' })}
                        </Button>
                      ) : (
                        <Button
                          type="primary"
                          onClick={() => setStep(2)}
                          css={style.mobileActionButton}>
                          {intl.formatMessage({ id: 'continue' })}
                        </Button>
                      )}
                    </div>
                  </Fragment>
                ) : step === 2 ? (
                  <Fragment>
                    <div css={style.mobileCommitCard}>
                      <div css={style.mobileCommitTitle}>
                        {intl.formatMessage({ id: 'enter-upload-message' })}
                      </div>

                      <Input.TextArea
                        css={style.mobileCommitTextarea}
                        rows={4}
                        placeholder={intl.formatMessage({
                          id: 'enter-upload-message',
                        })}
                        value={commitMessage}
                        onChange={({ target: { value } }) =>
                          setCommitMessage(value)
                        }
                      />

                      <Checkbox
                        css={style.mobileCheckbox}
                        checked={importantVal}
                        onChange={({ target: { checked } }) =>
                          setImportantVal(checked)
                        }>
                        {intl.formatMessage({
                          id: 'select-uploads-as-important',
                        })}
                      </Checkbox>
                    </div>

                    <div css={style.mobileActions}>
                      <Button
                        type="default"
                        onClick={() => setStep(1)}
                        css={style.mobileActionButton}>
                        {intl.formatMessage({ id: 'back' })}
                      </Button>

                      <Button
                        type="primary"
                        loading={isLoading}
                        disabled={!isValid() || isLoading}
                        onClick={handleUploadClick}
                        css={style.mobileActionButton}>
                        {intl.formatMessage({ id: 'upload' })}
                      </Button>
                    </div>
                  </Fragment>
                ) : null}
              </div>
            </div>
          </Collapse.Panel>
        </Collapse>

        {isLoading && (
          <div css={style.overlay}>
            <Spin size="large" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div css={style.container}>
      <Collapse
        bordered={false}
        defaultActiveKey={['uploadPanel']}
        css={style.properties}
        expandIconPosition="right"
        onChange={(...args) =>
          onCollapse && setTimeout(() => onCollapse(...args), 200)
        }>
        <Collapse.Panel header={panelHeader} key="uploadPanel">
          <div css={style.panelContainer}>
            <div css={style.panelTitle}>
              {icon('far', '', style.titleBtn)}
              {intl.formatMessage(
                { id: 'upload-panel-header' },
                { noOfFiles: uploadContext.length }
              )}
            </div>

            <div css={style.centerContentContainer}>
              {step === 1 ? (
                <Fragment>
                  {currentFolderRef.current?.option?.is_tender_folder
                    ? panelWarning
                    : null}
                  <Table
                    showSorterTooltip={false}
                    columns={isOnTender ? tenderColumns : columns}
                    dataSource={uploadContext}
                    rowClassName="editable-row"
                    rowKey={(row) => `${row.name}@${row.webkitRelativePath}`}
                    pagination={false}
                    showHeader
                    scroll={{ y: '21vh', x: 900 }}
                    css={style.table}
                  />
                  <div>
                    <Button
                      type="default"
                      onClick={() => setUploadContext([])}
                      css={style.buttons}>
                      {intl.formatMessage({ id: 'cancel' })}
                    </Button>
                    {isOnTender ? (
                      <Button
                        type="primary"
                        onClick={handleTenderUploadClick}
                        css={style.buttons}>
                        {intl.formatMessage({ id: 'upload' })}
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        onClick={() => setStep(2)}
                        css={style.buttons}>
                        {intl.formatMessage({ id: 'continue' })}
                      </Button>
                    )}
                  </div>
                </Fragment>
              ) : step === 2 ? (
                <Fragment>
                  <Input.TextArea
                    css={{
                      width: '100%',
                      animation: `${fadeInKeyframes} 0.3s ease-out`,
                    }}
                    rows={3}
                    placeholder={intl.formatMessage({
                      id: 'enter-upload-message',
                    })}
                    // autoFocus
                    value={commitMessage}
                    onChange={({ target: { value } }) =>
                      setCommitMessage(value)
                    }
                  />

                  <Checkbox
                    css={style.checkbox}
                    checked={importantVal}
                    onChange={({ target: { checked } }) =>
                      setImportantVal(checked)
                    }>
                    {intl.formatMessage({ id: 'select-uploads-as-important' })}
                  </Checkbox>

                  <div style={{ marginTop: -10 }}>
                    <Button
                      type="default"
                      onClick={() => setStep(1)}
                      css={style.buttons}>
                      {intl.formatMessage({ id: 'back' })}
                    </Button>

                    <Button
                      type="primary"
                      css={style.buttons}
                      loading={isLoading}
                      disabled={!isValid() || isLoading}
                      onClick={handleUploadClick}>
                      {intl.formatMessage({ id: 'upload' })}
                    </Button>
                  </div>
                </Fragment>
              ) : null}
            </div>
          </div>
        </Collapse.Panel>
      </Collapse>

      {isLoading && (
        <div css={style.overlay}>
          <Spin size="large" />
        </div>
      )}
    </div>
  );
};

export default DetailedUploadPanel;

export function incrementRevision(revision: string): string {
  if (revision.includes('.')) return revision;

  const match = revision.match(/^(.*?)([A-Za-z]+|\d+)$/);
  if (!match) return revision;

  const [, prefix, suffix] = match;

  if (/^\d+$/.test(suffix)) {
    return `${prefix}${parseInt(suffix, 10) + 1}`;
  }

  if (/^[A-Za-z]+$/.test(suffix)) {
    return `${prefix}${incrementAlphaSmart(suffix)}`;
  }

  return revision;
}

export function incrementAlphaSmart(str: string): string {
  const last = str[str.length - 1];
  const base = str.slice(0, -1);

  if (last === 'Z') return `${base}AA`;
  if (last === 'z') return `${base}aa`;

  const nextChar = String.fromCharCode(last.charCodeAt(0) + 1);
  return `${base}${nextChar}`;
}

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    position: 'relative',
    overflow: 'hidden',
  },
  containerMobile: {
    position: 'fixed',
    width: '100%',
    bottom: 60,
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
  },
  div: {
    color: theme.colors.text.primary,
    padding: '6px 0px',
  },
  properties: {
    color: theme.colors.text.secondary,
    backgroundColor: `${theme.colors.lightGreen} !important`,
  },
  panelContainer: {
    marginTop: '-35px',
  },
  centerContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  panelTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: theme.colors.text.primary,
  },
  removeIcon: {
    color: theme.colors.red,
  },
  checkbox: {
    marginTop: 6,
    alignSelf: 'flex-start',
  },
  buttons: {
    margin: ' 10px 10px 0 10px',
  },
  editableCell: {
    display: 'flex',
    width: '100%',
  },
  editableInput: {
    width: '100%',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0,0,0,.15)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  table: {
    width: '100%',
    height: '100%',
    animation: `${fadeInKeyframes} 0.3s ease-out`,
    '.ant-table-row': {
      animation: `${fadeInKeyframes} 0.3s ease-out`,
    },
  },
  mobileProperties: {
    color: theme.colors.text.secondary,
    backgroundColor: `${theme.colors.lightGreen} !important`,

    '.ant-collapse-content-box': {
      padding: '10px 12px 12px',
    },
  },

  mobilePanelContainer: {
    marginTop: '-24px',
  },

  mobilePanelTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 10,
    color: theme.colors.text.primary,
    fontSize: 15,
    fontWeight: 700,
  },

  mobileStepBadge: {
    flexShrink: 0,
    padding: '3px 8px',
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.75)',
    color: theme.colors.text.secondary,
    fontSize: 11,
    fontWeight: 600,
    lineHeight: '16px',
  },

  mobileContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    animation: `${fadeInKeyframes} 0.3s ease-out`,
  },

  mobileFilesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxHeight: '42vh',
    overflowY: 'auto',
    paddingRight: 2,
  },

  mobileFileCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 12,
    borderRadius: 10,
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.06)',
  },

  mobileFileHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  },

  mobileFileIndex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 24,
    height: 24,
    borderRadius: 999,
    backgroundColor: theme.colors.lightGreen,
    color: theme.colors.text.primary,
    fontSize: 12,
    fontWeight: 700,
  },

  mobileFileInfo: {
    minWidth: 0,
    flex: 1,
  },

  mobileFileName: {
    color: theme.colors.text.primary,
    fontSize: 13,
    fontWeight: 700,
    lineHeight: '18px',
    wordBreak: 'break-word',
  },

  mobileFileMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    marginTop: 3,
    color: theme.colors.text.secondary,
    fontSize: 11,
    lineHeight: '15px',
  },

  mobileFilePath: {
    wordBreak: 'break-word',
    opacity: 0.85,
  },

  mobileRemoveIcon: {
    color: theme.colors.red,
    fontSize: 16,
  },

  mobileField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },

  mobileFieldLabel: {
    color: theme.colors.text.secondary,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },

  mobileEditableRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },

  mobileEditableInput: {
    width: '100%',
    minHeight: 34,
  },

  mobileCopyIcon: {
    flexShrink: 0,
    color: theme.colors.text.secondary,
  },

  mobileSelect: {
    width: '100%',
  },

  mobileActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    paddingTop: 2,
  },

  mobileActionButton: {
    flex: 1,
    height: 36,
    margin: 0,
  },

  mobileCommitCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 12,
    borderRadius: 10,
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.06)',
  },

  mobileCommitTitle: {
    color: theme.colors.text.primary,
    fontSize: 13,
    fontWeight: 700,
  },

  mobileCommitTextarea: {
    width: '100%',
    resize: 'none',
  },

  mobileCheckbox: {
    marginTop: 0,
    alignSelf: 'flex-start',
  },
});
